import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/hooks/useSeo";
import { api, apiError } from "@/lib/api";
import { PHONES, intl, waChatLink } from "@/lib/whatsapp";
import { Phone, MessageCircle, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const { t, user } = useApp();
  const c = t.contact;
  useSeo({ title: c.title, description: c.sub });
  const [form, setForm] = useState({ name: user?.name || "", phone: "", email: user?.email || "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60 focus:border-tigris outline-none text-sm";
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/service-requests", { service: "contact", name: form.name, phone: form.phone, email: form.email || null, notes: form.notes });
      setSent(data.ref); toast.success(c.sent);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  return (
    <div className="bg-sand min-h-screen" data-testid="contact-page">
      <section className="bg-tigris text-white px-4 sm:px-6 pt-12 pb-20">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold font-display mb-3">{c.title}</h1>
          <p className="text-white/80 max-w-2xl">{c.sub}</p>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 -mt-10 pb-20 grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8">
          <h2 className="text-base md:text-lg font-bold text-slate-800 mb-5 flex items-center gap-2"><Phone size={18} className="text-gold" /> {c.phones}</h2>
          <ul className="space-y-3">
            {PHONES.map((p) => (
              <li key={p} className="flex items-center justify-between gap-3 bg-slate-50 rounded-xl px-4 py-3">
                <a href={`tel:+${intl(p)}`} dir="ltr" data-testid={`contact-phone-${p}`} className="font-bold text-slate-800 hover:text-tigris">{p}</a>
                <a href={waChatLink(t.common.waGreeting, intl(p))} target="_blank" rel="noreferrer" data-testid={`contact-wa-${p}`}
                  className="btn-wa inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"><MessageCircle size={14} /> WhatsApp</a>
              </li>
            ))}
          </ul>
        </div>
        <div className="space-y-6">
          <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 space-y-3">
            <h2 className="text-base md:text-lg font-bold text-slate-800 flex items-center gap-2"><Send size={18} className="text-gold" /> {c.formTitle}</h2>
            {sent ? (
              <div className="bg-green-50 text-green-700 rounded-xl p-4 text-sm font-semibold" data-testid="contact-success">{c.sent} <span className="font-mono">{sent}</span></div>
            ) : (
              <>
                <input data-testid="contact-name" className={inputCls} placeholder={c.name} value={form.name} onChange={(e) => set("name", e.target.value)} required />
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="contact-phone" dir="ltr" className={inputCls} placeholder={c.phone} value={form.phone} onChange={(e) => set("phone", e.target.value)} required />
                  <input data-testid="contact-email" type="email" dir="ltr" className={inputCls} placeholder={c.emailOpt} value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
                <textarea data-testid="contact-message" rows={4} className={inputCls} placeholder={c.message} value={form.notes} onChange={(e) => set("notes", e.target.value)} required minLength={3} />
                <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-gold w-full py-3 rounded-xl font-bold flex justify-center">{loading ? <Loader2 size={18} className="animate-spin" /> : c.send}</button>
              </>
            )}
          </form>
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 sm:p-8 space-y-4">
            <div className="flex items-start gap-3"><MapPin size={18} className="text-gold mt-0.5" /><div><div className="text-xs text-slate-400 font-semibold">{c.address}</div><div className="font-bold text-slate-800">{c.addressValue}</div></div></div>
            <div className="flex items-start gap-3"><Mail size={18} className="text-gold mt-0.5" /><div><div className="text-xs text-slate-400 font-semibold">{c.email}</div><a href="mailto:info@safeerbaghdad.iq" dir="ltr" className="font-bold text-slate-800 hover:text-tigris">info@safeerbaghdad.iq</a></div></div>
            <div className="flex items-start gap-3"><Clock size={18} className="text-gold mt-0.5" /><div><div className="text-xs text-slate-400 font-semibold">{c.hours}</div><div className="font-bold text-slate-800">{c.hoursValue}</div></div></div>
          </div>
          <a href={waChatLink(t.common.waGreeting)} target="_blank" rel="noreferrer" data-testid="contact-main-wa"
            className="btn-wa w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 text-base"><MessageCircle size={20} /> {t.common.chatWhatsapp}</a>
        </div>
      </section>
    </div>
  );
}
